import React, { forwardRef } from "react";
import "./OneClickSection.css";
import { motion } from "framer-motion";
import { MdOutlineTrackChanges, MdOutlineCampaign, MdInsights } from "react-icons/md";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const cards = [
  {
    icon: <MdOutlineTrackChanges />,
    step: "01",
    title: "Define success together",
    desc: "Align on audiences, budgets, and realistic KPIs before any spend. We document what “good” looks like for your business—not vanity metrics.",
    bullets: ["Goal workshop", "Budget reality check", "Tracking readiness"],
  },
  {
    icon: <MdOutlineCampaign />,
    step: "02",
    title: "Launch with quality gates",
    desc: "Campaigns, content, and tags go live with checklists so ads, landing pages, and analytics tell one consistent story.",
    bullets: ["Creative & copy QA", "UTM + event naming", "Policy-friendly claims"],
  },
  {
    icon: <MdInsights />,
    step: "03",
    title: "Learn and iterate",
    desc: "Regular readouts, a clear test backlog, and honest commentary. Improvement is the aim—not guaranteed lift every week.",
    bullets: ["Readable dashboards", "Structured tests", "Actionable reports"],
  },
];

const OneClickSection = forwardRef((_, ref) => {
  return (
    <section className="ocs" ref={ref} aria-labelledby="ocs-heading">
      <div className="ocs__wrap">
        <motion.div
          className="ocs__head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.65 }}
        >
          <p className="ocs__eyebrow">Engagement model</p>
          <h2 id="ocs-heading" className="ocs__title">
            From plan to proof—without the hype
          </h2>
          <p className="ocs__sub">
            One-time audits or ongoing management: we keep communication straightforward so you always know what we
            control versus what ad platforms and algorithms decide.
          </p>
        </motion.div>

        <motion.div
          className="ocs__grid"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {cards.map((c) => (
            <motion.article key={c.title} className="ocs__card" variants={item}>
              <div className="ocs__top">
                <div className="ocs__icon" aria-hidden="true">
                  {c.icon}
                </div>
                <span className="ocs__step">Step {c.step}</span>
              </div>
              <h3 className="ocs__h4">{c.title}</h3>
              <p className="ocs__p">{c.desc}</p>
              <ul className="ocs__bullets">
                {c.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </motion.article>
          ))}
        </motion.div>

        <motion.p
          className="ocs__rhythm"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <strong>Typical rhythm:</strong> Kickoff &amp; access → baseline &amp; launch → weekly or biweekly check-ins →
          monthly strategy review.
        </motion.p>
      </div>
    </section>
  );
});

OneClickSection.displayName = "OneClickSection";

export default OneClickSection;
